import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock, User, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchArticle();
  }, [id]);
  
  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="container py-16" data-testid="article-loading">
        <div className="text-center">
          <p className="text-xl text-stone-600">טוען מאמר...</p>
        </div>
      </div>
    );
  }
  
  if (!article) {
    return (
      <div className="container py-16" data-testid="article-not-found">
        <div className="text-center py-16">
          <h2 className="text-3xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            המאמר לא נמצא
          </h2>
          <button
            onClick={() => navigate('/articles')}
            className="h-12 px-8 rounded-full bg-[#D97706] text-white font-bold hover:shadow-lg transition-all duration-300"
            style={{ fontFamily: 'Heebo, sans-serif' }}
          >
            חזור למאמרים
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="article-detail-page">
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/articles')}
          data-testid="back-to-articles"
          className="absolute top-8 right-8 flex items-center gap-2 bg-white/90 hover:bg-white px-6 py-3 rounded-full text-[#2C3E2D] font-medium transition-all backdrop-blur-sm"
        >
          <ArrowRight className="w-5 h-5" />
          <span>חזור למאמרים</span>
        </button>
        
        {/* Category Badge */}
        <div className="absolute bottom-8 right-8">
          <span className="inline-block text-sm font-semibold text-white bg-[#D97706] px-4 py-2 rounded-full">
            {article.category}
          </span>
        </div>
      </div>
      
      {/* Article Content */}
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Title & Meta */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-6 leading-tight" style={{ fontFamily: 'Heebo, sans-serif' }} data-testid="article-title">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-stone-600 pb-6 border-b border-stone-200">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#D97706]" />
                <span className="font-medium">{article.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D97706]" />
                <span>{article.read_time} דקות קריאה</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#D97706]" />
                <span>{new Date(article.created_at).toLocaleDateString('he-IL')}</span>
              </div>
            </div>
            
            {/* Excerpt */}
            <div className="mt-6">
              <p className="text-xl text-stone-700 leading-relaxed">
                {article.excerpt}
              </p>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm" data-testid="article-content">
            <div className="prose prose-lg max-w-none" style={{ direction: 'rtl' }}>
              {article.content.split('\n').map((paragraph, index) => {
                // Handle bold headers (text between **)
                if (paragraph.includes('**')) {
                  const parts = paragraph.split('**');
                  return (
                    <h3 key={index} className="text-2xl font-bold text-[#2C3E2D] mt-8 mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
                      {parts[1] || parts[0]}
                    </h3>
                  );
                }
                
                // Handle bullet points
                if (paragraph.trim().startsWith('-')) {
                  return (
                    <li key={index} className="text-stone-700 leading-relaxed mb-2 mr-6">
                      {paragraph.trim().substring(1).trim()}
                    </li>
                  );
                }
                
                // Regular paragraphs
                if (paragraph.trim()) {
                  return (
                    <p key={index} className="text-stone-700 leading-relaxed mb-6 text-lg">
                      {paragraph}
                    </p>
                  );
                }
                
                return null;
              })}
            </div>
            
            {/* CTA to Products - Based on Category */}
            {article.category === 'שיפור האון הגברי' && (
              <div className="mt-12 bg-gradient-to-br from-[#D97706]/10 to-[#2C3E2D]/10 rounded-2xl p-8 border-2 border-[#D97706]" data-testid="product-cta">
                <h3 className="text-2xl font-bold text-[#2C3E2D] mb-4 text-center" style={{ fontFamily: 'Heebo, sans-serif' }}>
                  מוצרים מומלצים לשיפור האון הגברי
                </h3>
                <p className="text-center text-stone-600 mb-6">
                  המוצרים שלנו נועדו לתמוך בבריאות המינית שלך באופן טבעי ובטוח
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/products')}
                    className="h-14 px-10 rounded-full bg-[#D97706] text-white font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    style={{ fontFamily: 'Heebo, sans-serif' }}
                  >
                    צפה במוצרים לחיזוק האון הגברי ←
                  </button>
                </div>
              </div>
            )}
            
            {article.category === 'תזונה נכונה' && (
              <div className="mt-12 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 border-2 border-green-200" data-testid="product-cta">
                <h3 className="text-2xl font-bold text-[#2C3E2D] mb-4 text-center" style={{ fontFamily: 'Heebo, sans-serif' }}>
                  תוספי תזונה לבריאות מיטבית
                </h3>
                <p className="text-center text-stone-600 mb-6">
                  השלם את התזונה שלך עם תוספים איכותיים שיתמכו במטרות הבריאות שלך
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/products')}
                    className="h-14 px-10 rounded-full bg-green-600 text-white font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    style={{ fontFamily: 'Heebo, sans-serif' }}>
                    צפה בתוספי התזונה שלנו ←
                  </button>
                </div>
              </div>
            )}
            
            {article.category === 'אימונים ותרגילים' && (
              <div className="mt-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border-2 border-blue-200" data-testid="product-cta">
                <h3 className="text-2xl font-bold text-[#2C3E2D] mb-4 text-center" style={{ fontFamily: 'Heebo, sans-serif' }}>
                  מוצרים לתמיכה באימונים
                </h3>
                <p className="text-center text-stone-600 mb-6">
                  שפר את הביצועים וההתאוששות עם המוצרים המתקדמים שלנו
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/products')}
                    className="h-14 px-10 rounded-full bg-blue-600 text-white font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    style={{ fontFamily: 'Heebo, sans-serif' }}>
                    צפה במוצרים לספורטאים ←
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Related Articles CTA */}
          <div className="mt-12 bg-[#F5F5F4] rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
              מעוניין במאמרים נוספים?
            </h3>
            <p className="text-stone-600 mb-6">
              חקור עוד מאמרים בנושאי בריאות, תזונה ואימונים
            </p>
            <button
              onClick={() => navigate('/articles')}
              className="h-12 px-8 rounded-full bg-[#D97706] text-white font-bold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              style={{ fontFamily: 'Heebo, sans-serif' }}
            >
              צפה בכל המאמרים
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;